// 展示字段映射移植自 CherryWeb pages/b2c/simple-mall.utils.ts。
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

export interface CrowdFundingPreview {
  title: string;
  description: string;
  price?: string | number;
  coverImages: string[];
  officialVerification?: string;
  shelfTags: string[];
  presellText: string;
  deliveryMethods: string[];
  services: string[];
  seller?: {
    avatar: string;
    name: string;
  };
  detailText: string;
  detailImages: string[];
  skus: PreviewSku[];
}

export interface PreviewSku {
  id: string;
  name: string;
  specLabels: string[];
  cover?: string;
  price?: number;
  isSoldOut: boolean;
}

interface SkuSource {
  [key: string]: unknown;
  specs?: Array<{ value?: unknown }>;
}

const DELIVERY_PATTERN_LABEL_MAP: Record<string, string> = {
  HOST: "暂存柜发货",
  EXPRESS: "物流发货",
  PICK_UP: "自提",
  NO_NEED_EXPRESS: "无需物流",
  REPLACE_HOST: "寄存柜代发",
  ONLINE_DELIVERY: "在线履约",
  AUTO_DELIVERY: "自动发货",
};

const SHANGHAI_TIMEZONE = "Asia/Shanghai";

dayjs.extend(utc);
dayjs.extend(timezone);

function normalizeText(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeImageList(values: unknown) {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value) => normalizeText(value))
    .filter((value) => value.length > 0);
}

function normalizePresellBefore(value: unknown) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 0;
  }

  return numericValue < 1e12 ? numericValue * 1000 : numericValue;
}

function normalizeDeliveryPattern(value: unknown) {
  const rawValue = normalizeText(value);

  if (!rawValue) {
    return "";
  }

  return rawValue.startsWith("DeliverPattern_")
    ? rawValue.slice("DeliverPattern_".length)
    : rawValue;
}

function normalizeSkuName(value: unknown) {
  const name = normalizeText(value);

  return name || "未命名规格";
}

function normalizeSkuPrice(value: unknown) {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "string" && !value.trim()) {
    return undefined;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : undefined;
}

function buildPublicSimpleMallSkus(
  shelfDetail: Record<string, any>,
  staticDetail: Record<string, any>
) {
  const pspus = Array.isArray(shelfDetail.pspus) ? shelfDetail.pspus : [];
  const staticProducts = Array.isArray(staticDetail.products)
    ? staticDetail.products
    : [];
  const staticCoverByProductId = new Map(
    staticProducts
      .map((product) => {
        const productId =
          normalizeText(product?.id) || normalizeText(product?.productId);
        const cover = normalizeText(product?.cover);

        return [productId, cover] as const;
      })
      .filter(([productId, cover]) => productId && cover)
  );

  return pspus.flatMap((pspu, pspuIndex) => {
    const products: SkuSource[] = Array.isArray(pspu?.products)
      ? pspu.products
      : [];
    const pspuCover = normalizeText(pspu?.pspu?.images?.coverImages?.[0]);

    return products
      .filter((product) => product?.disable !== true)
      .map((product, productIndex) => {
        const productId = normalizeText(product?.productId) || "sku";

        return {
          id: `${productId}-${pspuIndex}-${productIndex}`,
          name: normalizeSkuName(product?.name),
          specLabels: Array.isArray(product?.specs)
            ? product.specs
                .map((spec) => normalizeText(spec?.value))
                .filter(Boolean)
            : [],
          cover:
            normalizeText(product?.cover) ||
            staticCoverByProductId.get(productId) ||
            pspuCover ||
            undefined,
          price: normalizeSkuPrice(product?.price),
          isSoldOut: product?.isSoldOut === true,
        };
      });
  });
}

function buildLegacySpecLabels(product: SkuSource, shelfName: string) {
  if (Array.isArray(product?.specs)) {
    return product.specs
      .map((spec) => normalizeText(spec?.value))
      .filter(Boolean);
  }

  const productName = normalizeText(product?.name);
  if (!shelfName || !productName.startsWith(shelfName)) {
    return [];
  }

  const nameSuffix = productName.slice(shelfName.length).trim();
  return nameSuffix ? [nameSuffix] : [];
}

function buildLegacySimpleMallSkus(staticDetail: Record<string, any>) {
  const products: SkuSource[] = Array.isArray(staticDetail.products)
    ? staticDetail.products
    : [];
  const b2cRules: SkuSource[] = Array.isArray(staticDetail.rules?.b2cRules)
    ? staticDetail.rules.b2cRules
    : [];
  const ruleByProductId = new Map(
    b2cRules
      .map((rule) => [normalizeText(rule?.productId), rule] as const)
      .filter(([productId]) => productId)
  );
  const shelfName = normalizeText(staticDetail.name);

  return products
    .map((product, productIndex) => {
      const productId =
        normalizeText(product?.id) || normalizeText(product?.productId);
      const rule = ruleByProductId.get(productId);

      return {
        product,
        productIndex,
        productId: productId || "sku",
        rule,
      };
    })
    .filter(({ product, rule }) => {
      return product?.disable !== true && rule?.disable !== true;
    })
    .map(({ product, productIndex, productId, rule }) => {
      const productName =
        normalizeText(product?.name) || normalizeText(rule?.name);

      return {
        id: `${productId}-legacy-${productIndex}`,
        name: normalizeSkuName(productName),
        specLabels: buildLegacySpecLabels(
          {
            ...product,
            name: productName,
          },
          shelfName
        ),
        cover: normalizeText(product?.cover) || undefined,
        price:
          normalizeSkuPrice(rule?.price) ?? normalizeSkuPrice(product?.price),
        isSoldOut:
          product?.isSoldOut === true || staticDetail.isSoldOut === true,
      };
    });
}

function buildSimpleMallSkus(
  shelfDetail: Record<string, any>,
  staticDetail: Record<string, any>
) {
  const pspus = Array.isArray(shelfDetail.pspus) ? shelfDetail.pspus : [];
  const hasPublicProducts = pspus.some((pspu) => {
    return Array.isArray(pspu?.products) && pspu.products.length > 0;
  });

  return hasPublicProducts
    ? buildPublicSimpleMallSkus(shelfDetail, staticDetail)
    : buildLegacySimpleMallSkus(staticDetail);
}

export function buildCrowdFundingPreview(
  input: {
    staticDetail?: Record<string, any> | null;
    detailUniversal?: Record<string, any> | null;
    simpleListRow?: Record<string, any> | null;
    shelfDetail?: Record<string, any> | null;
  },
  now = Date.now()
): CrowdFundingPreview {
  const staticDetail = input.staticDetail ?? {};
  const detailUniversal = input.detailUniversal ?? {};
  const simpleListRow = input.simpleListRow ?? {};
  const shelfDetail = input.shelfDetail ?? {};

  const meta = staticDetail.meta ?? {};
  const images = staticDetail.images ?? {};
  const patterns = Array.isArray(staticDetail.deliverPatternsDesc)
    ? staticDetail.deliverPatternsDesc
    : [];
  const labels = Array.isArray(simpleListRow.labels)
    ? simpleListRow.labels
    : [];
  const org = simpleListRow.org ?? {};
  const shelfDetailSeller = shelfDetail.seller ?? {};
  const shelfDetailTags = Array.isArray(shelfDetail.tags)
    ? shelfDetail.tags
    : [];

  const sellerType =
    normalizeText(shelfDetailSeller.sellerType) ||
    normalizeText(org.sellerType);
  const verification = normalizeText(detailUniversal.verification);
  const isOfficial = ["BRAND_OWNED", "PLATFORM_OWNED"].includes(verification);
  const hasLateCompensation = Number(detailUniversal.shouldDeliverDays) > 0;

  const coverImages = normalizeImageList(images.coverImages);
  const fallbackCover = normalizeText(images.coverImage);
  const deliveryMethods = [
    ...new Set(
      patterns
        .map((value) => normalizeDeliveryPattern(value))
        .map((value) => DELIVERY_PATTERN_LABEL_MAP[value])
        .filter(Boolean)
    ),
  ];

  const presellBefore = normalizePresellBefore(meta.presellBefore);
  const presellText =
    presellBefore > now
      ? `预计 ${dayjs(presellBefore)
          .tz(SHANGHAI_TIMEZONE)
          .format("YYYY-MM-DD")} ${
          deliveryMethods.includes("物流发货") ? "前发货" : "后可发货"
        }`
      : "";

  const detailText = normalizeText(meta.detailDescription);
  const detailImages = normalizeImageList(images.introImages);
  const sellerName = normalizeText(org.nickname);
  const sellerAvatar = normalizeText(org.avatar);
  const shelfTags =
    sellerType === "USER"
      ? shelfDetailTags.length > 0
        ? shelfDetailTags
            .map((item) => normalizeText(item?.name))
            .filter(Boolean)
        : labels
            .map((item) => ({
              text: normalizeText(item?.text),
              order: Number(item?.order),
            }))
            .filter((item) => item.text)
            .sort((left, right) => left.order - right.order)
            .map((item) => item.text)
      : [];

  const services = [
    ...(isOfficial ? ["官方直购"] : []),
    ...(hasLateCompensation ? ["晚发必赔"] : []),
  ];

  return {
    title: normalizeText(staticDetail.name),
    description: normalizeText(staticDetail.description),
    price: normalizeSkuPrice(staticDetail.unitPriceOfCash),
    coverImages:
      coverImages.length > 0 ? coverImages : [fallbackCover].filter(Boolean),
    officialVerification:
      verification && verification !== "DEFAULT" ? verification : undefined,
    shelfTags,
    presellText,
    deliveryMethods,
    services,
    seller: sellerName
      ? {
          name: sellerName,
          avatar: sellerAvatar,
        }
      : undefined,
    detailText,
    detailImages,
    skus: buildSimpleMallSkus(shelfDetail, staticDetail),
  };
}

// productId 是历史分享链接中的货架 ID，不是 SKU ID。
export function resolveShelfId(query: Record<string, unknown>): string {
  const value = query.productId ?? query.shelfId;
  return typeof value === "string" && /^[1-9]\d*$/.test(value.trim())
    ? value.trim()
    : "";
}

export function crowdFundingAppTarget(shelfId: string) {
  return { targetPath: "crowd-funding", params: { productId: shelfId } };
}
